import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import Swal from 'sweetalert2';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import { showError, showSuccess } from '../../../../core/alert/sweetAlertConfig';

interface KeyRequest {
  id: number;
  keyIds: number[];
  reason: string;
  requestedBy: string;
  requestedDate: string;
  approvalStatus: string;
  approvedBy: string | null;
}

interface UserDTO {
  empNumber: string;
  name: string;
  createdAt: string;
  status: string;
  email: string;
  phone: string;
  userType: string;
  departmentNames: string[];
  licenseNumber: string;
  licenseType: string;
  expireDate: string;
}

interface ApprovedKeyRequestTableProps {
  data: KeyRequest[];
  /** Parent callback: call after a successful action so dashboard can refresh+close */
  onActionSuccess?: () => void;
}

const ApprovedKeyRequestTable: React.FC<ApprovedKeyRequestTableProps> = ({ data, onActionSuccess }) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showHandoverModal, setShowHandoverModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<KeyRequest | null>(null);

  const [users, setUsers] = useState<UserDTO[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [handoverType, setHandoverType] = useState<string>('PERMANENT');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  // MULTI-SELECT KEYS
  const [selectedKeyIds, setSelectedKeyIds] = useState<string[]>([]);
  const toggleKeyId = (id: string) => {
    setSelectedKeyIds(prev => (prev.includes(id) ? prev.filter(k => k !== id) : [...prev, id]));
  };
  const selectAllKeys = () => {
    if (!selectedRequest) return;
    setSelectedKeyIds((selectedRequest.keyIds || []).map(String));
  };
  const clearAllKeys = () => setSelectedKeyIds([]);

  // Fetch users for Handover To dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      if (!checkPermission('ROLE_GET_USERS')) {
        showError('You do not have permission to view users.');
        return;
      }
      setIsLoading(true);
      try {
        let page = 1;
        let hasMore = true;
        const allUsers: UserDTO[] = [];
        const payload = { filterType: '', filterValue: '', date: '' };
        while (hasMore) {
          const response = await apiDefinitions.getUsers(page, payload);
          if (response.data.status === 'S1000') {
            const usersPage = response.data.data.users;
            if (!usersPage || usersPage.length === 0) {
              hasMore = false;
              break;
            }
            const mappedUsers = usersPage
              .filter((user: any) => user.empNumber)
              .map((user: any) => ({
                empNumber: user.empNumber.toString(),
                name: user.name || 'Unknown',
                createdAt: user.createdAt
                  ? new Date(user.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                  : 'N/A',
                status: user.status || 'PENDING',
                email: user.email || '',
                phone: user.phone || '',
                userType: user.group?.replace('_K', '') || 'USER',
                departmentNames: user.departmentAdminSpecificData?.departmentNames?.length
                  ? [user.departmentAdminSpecificData.departmentNames[0]]
                  : [],
                licenseNumber: user.driverSpecificData?.licenseNumber || '',
                licenseType: user.driverSpecificData?.licenseType || '',
                expireDate: user.driverSpecificData?.expireDate
                  ? new Date(user.driverSpecificData.expireDate).toISOString().split('T')[0]
                  : '',
              }));
            allUsers.push(...mappedUsers);
            page++;
          } else {
            showError('Failed to fetch users: ' + response.data.statusDescription);
            break;
          }
        }
        setUsers(allUsers);
        if (allUsers.length === 0) showError('No users found.');
      } catch (e: any) {
        showError('Failed to fetch users. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const handleViewClick = async (id: number) => {
    setIsLoading(true);
    try {
      const res = await apiDefinitions.getKeyRequestById(id);
      if (res.data.status === 'S1000') {
        setSelectedRequest(res.data.data);
        setShowDetailsModal(true);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed',
          text: res.data.statusDescription || 'Failed to fetch request details.',
          confirmButtonText: 'OK',
        });
      }
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err?.response?.data?.statusDescription || 'Failed to fetch request details.',
        confirmButtonText: 'OK',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleHandoverClick = (request: KeyRequest) => {
    setSelectedRequest(request);
    setSelectedUser('');
    setHandoverType('PERMANENT');
    setSearchTerm('');
    // preselect none (or use selectAllKeys() if you prefer)
    setSelectedKeyIds([]);
    setShowHandoverModal(true);
  };

  const handleHandoverSubmit = async () => {
    if (!checkPermission('ROLE_MARK_HANDOVER')) {
      showError('You do not have permission to mark key handover.');
      return;
    }
    if (!selectedUser) {
      showError('Please select an employee to handover to.');
      return;
    }
    if (!selectedRequest) {
      showError('No key request selected.');
      return;
    }
    if (selectedKeyIds.length === 0) {
      showError('Please select at least one key to handover.');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        keyIds: selectedKeyIds.map(k => parseInt(k, 10)), // send ALL selected keys
        handoverTo: selectedUser,
        handoverType,
      };
      const res = await apiDefinitions.markHandover(payload);
      if (res.status === 200 && res.data.status === 'S1000') {
        showSuccess('Handover marked successfully');
        setShowHandoverModal(false);
        setSelectedRequest(null);
        setSelectedUser('');
        setSelectedKeyIds([]);
        setHandoverType('PERMANENT');
        setSearchTerm('');
        // notify parent to refresh + close its modal
        onActionSuccess?.();
      } else {
        showError(res.data?.statusDescription || 'Failed to mark handover.');
      }
    } catch (err: any) {
      showError(err.response?.data?.statusDescription || 'Failed to mark handover. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedRequest(null);
  };

  const handleCloseHandoverModal = () => {
    setShowHandoverModal(false);
    setSelectedRequest(null);
    setSelectedUser('');
    setSelectedKeyIds([]);
    setHandoverType('PERMANENT');
    setSearchTerm('');
  };

  const filteredUsers = users.filter(
    user =>
      user.empNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              <th>ID</th>
              <th>Key IDs</th>
              <th>Reason</th>
              <th>Requested By</th>
              <th>Requested Date</th>
              <th>Approved By</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center">No key requests available.</td>
              </tr>
            ) : (
              data.map(request => (
                <tr key={request.id}>
                  <td>{request.id}</td>
                  <td>{request.keyIds.join(', ')}</td>
                  <td>{request.reason}</td>
                  <td>{request.requestedBy}</td>
                  <td>
                    {new Date(request.requestedDate).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </td>
                  <td>{request.approvedBy || 'N/A'}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-info me-1"
                      onClick={() => handleViewClick(request.id)}
                      disabled={isLoading}
                      title="View Details"
                    >
                      <i className="ti ti-eye"></i>
                    </button>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => handleHandoverClick(request)}
                      disabled={isLoading || request.keyIds.length === 0}
                      title="Handover Keys"
                    >
                      <i className="ti ti-key"></i>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Details Modal */}
      <Modal show={showDetailsModal} onHide={handleCloseDetailsModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Approved Key Request Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest ? (
            <div>
              <p><strong>ID:</strong> {selectedRequest.id}</p>
              <p><strong>Key IDs:</strong> {selectedRequest.keyIds.join(', ')}</p>
              <p><strong>Reason:</strong> {selectedRequest.reason}</p>
              <p><strong>Requested By:</strong> {selectedRequest.requestedBy}</p>
              <p>
                <strong>Requested Date:</strong>{' '}
                {new Date(selectedRequest.requestedDate).toLocaleString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })}
              </p>
              <p><strong>Approval Status:</strong> {selectedRequest.approvalStatus}</p>
              <p><strong>Approved By:</strong> {selectedRequest.approvedBy || 'N/A'}</p>
            </div>
          ) : (
            <p>No details available.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseDetailsModal} disabled={isLoading}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Handover Modal */}
      <Modal show={showHandoverModal} onHide={handleCloseHandoverModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Handover Key</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* MULTI-SELECT KEYS */}
            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <Form.Label className="fw-semibold m-0">Select Keys</Form.Label>
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={selectAllKeys}
                    disabled={isLoading || !selectedRequest || selectedRequest.keyIds?.length === 0}
                  >
                    Select All
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={clearAllKeys}
                    disabled={isLoading || selectedKeyIds.length === 0}
                  >
                    Clear
                  </Button>
                </div>
              </div>

              {selectedRequest && selectedRequest.keyIds.length > 0 ? (
                selectedRequest.keyIds.map((keyId: number | string) => {
                  const idStr = String(keyId);
                  return (
                    <Form.Check
                      key={idStr}
                      type="checkbox"
                      id={`key-${idStr}`}
                      label={`Key ID: ${idStr}`}
                      value={idStr}
                      checked={selectedKeyIds.includes(idStr)}
                      onChange={() => toggleKeyId(idStr)}
                      disabled={isLoading}
                    />
                  );
                })
              ) : (
                <p className="mb-0">No keys available for this request.</p>
              )}

              {selectedKeyIds.length > 0 && (
                <div className="mt-2 text-muted small">
                  Selected: {selectedKeyIds.join(', ')}
                </div>
              )}
            </Form.Group>

            {/* Handover To */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Handover To</Form.Label>
              <div className="dropdown">
                <Form.Control
                  type="text"
                  placeholder={users.length === 0 ? 'No users available' : 'Search by name or emp ID'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={isLoading || users.length === 0}
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                />
                <ul className="dropdown-menu w-100" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {filteredUsers.length === 0 ? (
                    <li>
                      <span className="dropdown-item text-muted">No matching users</span>
                    </li>
                  ) : (
                    filteredUsers.map((user) => (
                      <li key={user.empNumber}>
                        <button
                          className="dropdown-item"
                          onClick={() => {
                            setSelectedUser(user.empNumber);
                            setSearchTerm(`${user.name} (${user.empNumber})`);
                          }}
                        >
                          {user.name} ({user.empNumber})
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </Form.Group>

            {/* Handover Type */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Handover Type</Form.Label>
              <Form.Select
                value={handoverType}
                onChange={(e) => setHandoverType(e.target.value)}
                disabled={isLoading}
              >
                <option value="PERMANENT">Permanent</option>
                <option value="TEMPORARY">Temporary</option>
              </Form.Select>
            </Form.Group>

            {/* Info */}
            {selectedRequest && (
              <div>
                <p><strong>Request ID:</strong> {selectedRequest.id}</p>
                <p><strong>Requested By:</strong> {selectedRequest.requestedBy}</p>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseHandoverModal} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleHandoverSubmit}
            disabled={isLoading || !selectedUser || selectedKeyIds.length === 0}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processing...
              </>
            ) : (
              'Handover'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ApprovedKeyRequestTable;
